/**
 * Shared snap-ref helpers for the Construction tool and its extracted
 * sub-controllers (e.g. `RotationController`). These are tiny pure
 * functions plus one shared hint string that both `ConstructionTool`
 * and the rotation machinery need — pulling them into their own module
 * lets the controller import them without a circular dependency on
 * `ConstructionTool`.
 */
import type { ConstraintRef } from "../model/Project";
import type { SnapRefLite } from "../model/constraintEnforcement";

/** Issue #180 follow-up: copy displayed in the transient hint
 *  whenever a bidirectional mirror edit is refused. Same message
 *  for every refusal site so the user learns one rule: the chain
 *  bottomed out at an offset, so the inverse-edit math has no
 *  closed form — point them at the source line instead. */
export const MIRROR_OFFSET_REFUSE_HINT =
  "Edit the source line directly — can't route through an offset.";

export function snapRefToConstraintRef(r: SnapRefLite): ConstraintRef {
  if (r.kind === "anchor") {
    return {
      kind: "anchor",
      constructionId: r.constructionId,
      pathIndex: r.pathIndex,
      segmentIndex: r.segmentIndex,
    };
  }
  if (r.kind === "center") {
    return { kind: "center", constructionId: r.constructionId };
  }
  if (r.kind === "bbox-midpoint") {
    return {
      kind: "bbox-midpoint",
      constructionId: r.constructionId,
      edge: r.edge,
    };
  }
  // Segment-midpoints are position-only snap targets (issue #377) and
  // have no persistable `ConstraintRef`. Callers gate constraint
  // creation on `isPinnableSnapRef`, so this is never reached.
  throw new Error("segment-midpoint snap refs cannot become constraints");
}

/** Whether a snap candidate can be pinned with a coincident
 *  constraint. Segment-midpoints (issue #377) snap the cursor into
 *  place like a grid snap but don't persist a constraint. */
export function isPinnableSnapRef(r: SnapRefLite): boolean {
  return r.kind !== "segment-midpoint";
}
